import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../UIToolkit/Popups/Dialog";

type Props = {
	open: boolean;
	/** The employee's single job, named in the first sentence. */
	jobName: string;
	onClose: () => void;
	/**
	 * Supplied only from the daily view, where the profile is a navigation away. Its presence is
	 * what switches the dialog between the two-button (navigate) and single-button (dismiss) shapes,
	 * and drops the "open the profile" step when the user is already on the profile page.
	 */
	onGoToProfile?: () => void;
};

/**
 * Shown when a scheduler clicks the job on an actual shift worked by an employee who holds exactly
 * one job (DAT-615). There is nothing to reassign to, so instead of an inert label the dialog spells
 * out the flow — add a second job on the profile, then come back and switch the shift.
 *
 * Multi-job employees get `EditJobPopover` instead, which reassigns the job directly (DAT-502).
 */
const ActualShiftJobHintDialog: FC<Props> = ({ open, jobName, onClose, onGoToProfile }) => {
	const { t } = useTranslation();

	const steps = onGoToProfile
		? [t("actual.shift.single.job.step.open.profile"), t("actual.shift.single.job.step.add.job"), t("actual.shift.single.job.step.return.to.shift")]
		: [t("actual.shift.single.job.step.add.job"), t("actual.shift.single.job.step.update.shift")];

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			dataTestId="actual-shift-single-job-dialog"
			title={t("actual.shift.single.job.title")}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 460 }}>
					<Box>{t("actual.shift.single.job.intro", { jobName })}</Box>
					{/* A real <ol> keeps the step numbers out of the translations. */}
					<Box component="ol" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
						{steps.map((step) => (
							<Box component="li" key={step}>
								{step}
							</Box>
						))}
					</Box>
				</Box>
			}
			hideCancel={!onGoToProfile}
			confirm={onGoToProfile ? t("actual.shift.single.job.go.to.profile") : t("close")}
			confirmButtonVariant={onGoToProfile ? "primary" : "borderless"}
			onConfirm={onGoToProfile ?? onClose}
		/>
	);
};

export default ActualShiftJobHintDialog;
